import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { usePermissions } from "@/hooks/usePermissions";
import {
    Zap,
    Activity,
    Upload,
    LayoutDashboard,
    FileText,
    Users,
    Megaphone,
    ShieldCheck,
    TrendingUp,
    BarChart3,
    Settings,
    LogOut,
    Menu,
    X,
    Eye,
    MessageSquare,
    Smartphone,
    Globe,
    ChevronRight,
    Plus,
    Trophy,
    Rss,
    Radio,
    Calendar,
    Layers,
    ChevronDown,
    Send,
    UserCog,
    UserCheck,
    Sparkles,
    DollarSign,
    Search,
    Bell,
    Hash,
} from "lucide-react";

// Admin Layout Component
export const AdminLayout = ({ children, title }) => {
    const { user, logout } = useAuth();
    const { can } = usePermissions();
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Groups auto-expand when the current path matches any child
    const isInGroup = (group) =>
        group?.children?.some((c) => location.pathname === c.path);
    const [expandedGroups, setExpandedGroups] = useState(() => ({
        "reporting": true,
        "content-management": true,
        "customer-interactions": true,
    }));

    // Keep group expanded when route lands inside it
    useEffect(() => {
        navItems.forEach((it) => {
            if (it.group && isInGroup(it)) {
                setExpandedGroups((p) => ({ ...p, [it.group]: true }));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const toggleGroup = (groupId) =>
        setExpandedGroups((p) => ({ ...p, [groupId]: !p[groupId] }));

    const navItems = [
        {
            group: "reporting",
            label: "Reporting & Analytics",
            icon: BarChart3,
            permission: "view_analytics",
            children: [
                { path: "/admin", icon: LayoutDashboard, label: "Overview", permission: "view_dashboard" },
                { path: "/admin/live-ops", icon: Activity, label: "Live Ops", permission: "view_live_ops" },
                { path: "/admin/analytics", icon: BarChart3, label: "Analytics", permission: "view_analytics" },
                { path: "/admin/conversion-funnel", icon: TrendingUp, label: "Conversion Funnel", permission: "view_analytics" },
            ],
        },
        {
            group: "content-management",
            label: "Content Management",
            icon: Layers,
            children: [
                { path: "/admin/studio", icon: Upload, label: "Studio", permission: "create_content" },
                { path: "/admin/content-hub", icon: Radio, label: "Content Hub", permission: "create_content" },
                { path: "/admin/social-sources", icon: Hash, label: "Social Sources", permission: "create_content" },
                { path: "/admin/news-feed", icon: Rss, label: "News Feed", permission: "create_content" },
                { path: "/admin/content", icon: FileText, label: "Articles", permission: "edit_content" },
                { path: "/admin/matches", icon: Trophy, label: "Match Results", permission: "edit_content" },
                { path: "/admin/predictions", icon: TrendingUp, label: "Predictions", permission: "view_predictions" },
                { path: "/admin/schedules", icon: Calendar, label: "Fixtures", permission: "edit_content" },
            ],
        },
        {
            group: "customer-interactions",
            label: "Customer Interactions",
            icon: MessageSquare,
            permission: "manage_customers",
            children: [
                { path: "/admin/sms-interactions", icon: MessageSquare, label: "SMS Inbox & AI", permission: "manage_sms" },
                { path: "/admin/for-you", icon: Sparkles, label: "For-You Pulse", permission: "manage_customers" },
            ],
        },
        {
            group: "sms",
            label: "Campaigns & Push",
            icon: Megaphone,
            permission: "manage_campaigns",
            children: [
                { path: "/admin/campaigns", icon: Send, label: "Campaigns", permission: "manage_campaigns" },
                { path: "/admin/approvals", icon: ShieldCheck, label: "Approvals", permission: "approve_campaigns" },
                { path: "/admin/auto-sms", icon: Zap, label: "Auto-SMS", permission: "manage_sms" },
                { path: "/admin/prediction-push", icon: TrendingUp, label: "Push Predictions", permission: "manage_campaigns" },
                { path: "/admin/customer-bases", icon: Users, label: "Customer Bases", permission: "manage_customers" },
            ],
        },
        { path: "/admin/customers", icon: Users, label: "Customers", permission: "manage_customers" },
        { path: "/admin/subscriptions", icon: UserCheck, label: "Subscriptions", permission: "manage_customers" },
        { path: "/admin/transactions", icon: DollarSign, label: "Transactions", permission: "view_transactions" },
        { path: "/admin/users", icon: UserCog, label: "System Users", adminOnly: true, permission: "manage_users" },
        { path: "/admin/settings", icon: Settings, label: "Control & Management", adminOnly: true, permission: "manage_settings" },
    ];

    // Filter helper: hide a nav item when the user's role lacks the permission
    // (admins bypass in ``usePermissions.can``). Group items are hidden only
    // when they have NO visible children.
    const isVisible = (item) => {
        if (item.adminOnly && user?.role !== "admin") return false;
        if (item.permission && !can(item.permission)) return false;
        return true;
    };
    const filteredNavItems = navItems
        .filter((it) => isVisible(it))
        .map((it) => {
            if (!it.children) return it;
            const kids = it.children.filter(isVisible);
            return kids.length ? { ...it, children: kids } : null;
        })
        .filter(Boolean);

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    return (
        <div className="min-h-screen bg-obsidian flex" data-testid="admin-layout">
            {/* Sidebar */}
            <aside 
                className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-paper border-r border-border transform transition-transform lg:transform-none ${
                    sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
                }`}
            >
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="p-5 border-b border-border">
                        <Link to="/admin" className="flex items-center gap-2">
                            <Zap className="h-7 w-7 text-electric" />
                            <div>
                                <span className="dimbani-wordmark text-xl text-white block leading-tight">
                                    DIMBANI
                                </span>
                                <span className="text-white/40 text-[10px] uppercase tracking-wider">Admin Panel</span>
                            </div>
                        </Link>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                        {filteredNavItems.map((item) => {
                            // Grouped (collapsible) section
                            if (item.group) {
                                const expanded = !!expandedGroups[item.group];
                                const hasActiveChild = isInGroup(item);
                                return (
                                    <div key={item.group} className="space-y-0.5" data-testid={`nav-group-${item.group}`}>
                                        <button
                                            onClick={() => toggleGroup(item.group)}
                                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all ${
                                                hasActiveChild
                                                    ? "text-white"
                                                    : "text-white/60 hover:text-white hover:bg-subtle/50"
                                            }`}
                                            data-testid={`nav-group-toggle-${item.group}`}
                                        >
                                            <span className="flex items-center gap-2.5">
                                                <item.icon className="h-4 w-4" />
                                                <span className="font-heading font-bold text-[10px] uppercase tracking-[0.14em]">
                                                    {item.label}
                                                </span>
                                            </span>
                                            <ChevronDown
                                                className={`h-3.5 w-3.5 transition-transform ${
                                                    expanded ? "" : "-rotate-90"
                                                }`}
                                            />
                                        </button>
                                        {expanded && (
                                            <div className="ml-2 space-y-0.5">
                                                {item.children.map((child) => {
                                                    const isActive = location.pathname === child.path;
                                                    return (
                                                        <Link
                                                            key={child.path}
                                                            to={child.path}
                                                            className={`flex items-center gap-3 pl-6 pr-3 py-2 rounded-lg text-sm transition-all group ${
                                                                isActive
                                                                    ? "bg-electric text-white font-semibold shadow-[0_2px_8px_rgba(230,45,45,0.25)]"
                                                                    : "text-white/60 hover:text-white hover:bg-subtle/60"
                                                            }`}
                                                            onClick={() => setSidebarOpen(false)}
                                                            data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                        >
                                                            <child.icon className="h-4 w-4" />
                                                            <span className="font-medium flex-1">{child.label}</span>
                                                            {isActive && <ChevronRight className="h-4 w-4" />}
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            // Standalone top-level item
                            const isActive = location.pathname === item.path;
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                                        isActive
                                            ? "bg-electric text-white font-semibold shadow-[0_2px_8px_rgba(230,45,45,0.25)]"
                                            : "text-white/60 hover:text-white hover:bg-subtle/60"
                                    }`}
                                    onClick={() => setSidebarOpen(false)}
                                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <item.icon className="h-4 w-4" />
                                    <span className="font-medium text-sm flex-1">{item.label}</span>
                                    {isActive && <ChevronRight className="h-4 w-4" />}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* User & Logout */}
                    <div className="p-3 border-t border-border">
                        <div className="flex items-center gap-3 mb-3 px-2">
                            <div className="w-9 h-9 rounded-full bg-electric flex items-center justify-center flex-shrink-0">
                                <span className="text-white font-bold text-sm">
                                    {user?.full_name?.charAt(0) || "U"}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-white text-sm font-medium truncate">{user?.full_name}</p>
                                <Badge variant="outline" className="text-[10px] border-electric/30 text-electric mt-0.5">
                                    {user?.role}
                                </Badge>
                            </div>
                        </div>
                        <div className="flex gap-1">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="flex-1 text-white/60 hover:text-white h-8 text-xs"
                                onClick={() => navigate("/")}
                            >
                                <Globe className="h-3.5 w-3.5 mr-1" />
                                View Site
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300"
                                onClick={handleLogout}
                                data-testid="admin-logout-btn"
                            >
                                <LogOut className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main Content */}
            <main className="flex-1 min-h-screen">
                {/* Top Bar */}
                <header className="sticky top-0 z-30 bg-obsidian/95 backdrop-blur border-b border-border px-4 lg:px-8 py-4">
                    <div className="flex items-center gap-4">
                        <button
                            className="lg:hidden text-white"
                            onClick={() => setSidebarOpen(true)}
                            data-testid="mobile-menu-btn"
                        >
                            <Menu className="h-6 w-6" />
                        </button>
                        <h1 className="text-xl lg:text-2xl font-heading font-black text-white uppercase tracking-wide" data-testid="admin-page-title">
                            {title}
                        </h1>

                        {/* Global search — flexes into the remaining space */}
                        <div className="hidden md:flex flex-1 max-w-2xl mx-6 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 pointer-events-none" />
                            <Input
                                type="search"
                                placeholder="Search articles, customers, campaigns..."
                                className="pl-10 bg-paper border-border text-white h-10 rounded-full w-full"
                                data-testid="admin-global-search"
                            />
                        </div>

                        <div className="ml-auto flex items-center gap-3">
                            <button
                                className="relative h-9 w-9 rounded-full bg-paper border border-border flex items-center justify-center text-white/60 hover:text-white transition-colors"
                                data-testid="admin-notifications-btn"
                                aria-label="Notifications"
                            >
                                <Bell className="h-4 w-4" />
                                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-electric" />
                            </button>
                            <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-border">
                                <div className="w-8 h-8 rounded-full bg-electric flex items-center justify-center flex-shrink-0">
                                    <span className="text-white font-bold text-xs">
                                        {user?.full_name?.charAt(0) || "U"}
                                    </span>
                                </div>
                                <div className="hidden md:block">
                                    <div className="text-white text-xs font-medium leading-tight">{user?.full_name}</div>
                                    <div className="text-white/40 text-[10px] uppercase tracking-wider">{user?.role}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Content */}
                <div className="p-4 lg:p-8">
                    {children}
                </div>
            </main>
        </div>
    );
};

// Dashboard Page
const AdminDashboard = () => {
    const { token } = useAuth();
    const [analytics, setAnalytics] = useState(null);
    const [recentContent, setRecentContent] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const [analyticsRes, contentRes] = await Promise.all([
                axios.get(`${API}/analytics/overview`, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => ({ data: null })),
                axios.get(`${API}/content?limit=5`, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => ({ data: [] }))
            ]);
            setAnalytics(analyticsRes.data);
            setRecentContent(contentRes.data);
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    const stats = [
        {
            label: "Total Users",
            value: analytics?.total_users || 0,
            icon: Users,
            trend: "+11%",
            trendUp: true,
            sub: "Last 30 days",
        },
        {
            label: "Total Content",
            value: analytics?.total_content || 0,
            icon: FileText,
            trend: "+8%",
            trendUp: true,
            sub: "Last 30 days",
        },
        {
            label: "Total Views",
            value: (analytics?.total_views || 0).toLocaleString(),
            icon: Eye,
            trend: "+24%",
            trendUp: true,
            sub: "Last 30 days",
        },
        {
            label: "Revenue",
            value: `TZS ${(analytics?.revenue || 0).toLocaleString()}`,
            icon: DollarSign,
            trend: "+18%",
            trendUp: true,
            sub: "Last 30 days",
        },
    ];

    const channelStats = [
        { label: "Web", value: analytics?.views_by_channel?.web || 0, icon: Globe },
        { label: "SMS", value: analytics?.views_by_channel?.sms || 0, icon: MessageSquare },
        { label: "WhatsApp", value: analytics?.views_by_channel?.whatsapp || 0, icon: Smartphone },
    ];

    return (
        <AdminLayout title="Overview">
            <div className="space-y-6" data-testid="admin-dashboard">
                {/* Stats Grid — mockup style: title, subtitle, big value, icon, delta badge */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                    {stats.map((stat, idx) => (
                        <Card key={idx} className="bg-paper border-border rounded-2xl hover:shadow-lg transition-shadow" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <p className="text-white/90 font-semibold text-sm">{stat.label}</p>
                                        <p className="text-white/40 text-xs mt-0.5">{stat.sub}</p>
                                    </div>
                                    <div className="w-10 h-10 rounded-full bg-electric/10 flex items-center justify-center">
                                        <stat.icon className="h-4 w-4 text-electric" />
                                    </div>
                                </div>
                                <div className="flex items-end justify-between">
                                    <div className="text-3xl font-heading font-black text-white tabular-nums leading-none">
                                        {loading ? "…" : stat.value}
                                    </div>
                                    <Badge
                                        className={`text-[10px] border ${
                                            stat.trendUp
                                                ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
                                                : "bg-red-500/15 text-red-400 border-red-500/40"
                                        }`}
                                        data-testid={`stat-trend-${idx}`}
                                    >
                                        <TrendingUp className={`h-3 w-3 mr-1 ${stat.trendUp ? "" : "rotate-180"}`} />
                                        {stat.trend}
                                    </Badge>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Channel Distribution */}
                <Card className="bg-paper border-border rounded-2xl" data-testid="channel-stats">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-white font-heading uppercase text-lg tracking-wider">
                            Views by Channel
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {channelStats.map((channel, idx) => (
                                <div key={idx} className="p-4 bg-subtle/40 rounded-xl border border-border">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-9 h-9 rounded-full bg-electric/10 flex items-center justify-center">
                                            <channel.icon className="h-4 w-4 text-electric" />
                                        </div>
                                        <span className="text-white font-medium">{channel.label}</span>
                                    </div>
                                    <p className="text-2xl font-heading font-black text-white tabular-nums">
                                        {loading ? "…" : channel.value.toLocaleString()}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Recent Content */}
                <Card className="bg-paper border-border" data-testid="recent-content">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-white font-heading uppercase">
                            Recent Content
                        </CardTitle>
                        <Link to="/admin/content">
                            <Button variant="ghost" size="sm" className="text-electric">
                                View All
                                <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                        </Link>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="space-y-4">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="animate-pulse flex gap-4">
                                        <div className="w-20 h-14 bg-muted rounded" />
                                        <div className="flex-1 space-y-2">
                                            <div className="h-4 bg-muted rounded w-3/4" />
                                            <div className="h-3 bg-muted rounded w-1/2" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : recentContent.length > 0 ? (
                            <div className="space-y-4">
                                {recentContent.map((content, idx) => (
                                    <div key={content.id} className="flex items-center gap-4 p-3 bg-subtle rounded-sm hover:bg-border/50 transition-colors">
                                        <img
                                            src={content.image_url || "https://images.unsplash.com/photo-1650289246926-ca8eaa0c3325?w=100"}
                                            alt={content.title}
                                            className="w-20 h-14 object-cover rounded"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-white font-medium text-sm truncate">
                                                {content.title}
                                            </h4>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Badge 
                                                    variant="outline" 
                                                    className={`text-xs ${
                                                        content.status === "published" 
                                                            ? "border-electric text-electric" 
                                                            : "border-solar text-solar"
                                                    }`}
                                                >
                                                    {content.status}
                                                </Badge>
                                                <span className="text-white/40 text-xs">
                                                    {content.views_web + content.views_sms + content.views_whatsapp} views
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <FileText className="h-12 w-12 text-white/20 mx-auto mb-2" />
                                <p className="text-white/40">No content yet</p>
                                <Link to="/admin/content">
                                    <Button className="mt-4 btn-primary">
                                        <Plus className="h-4 w-4 mr-2" />
                                        Create Content
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Link to="/admin/content" className="block">
                        <Card className="bg-paper border-border hover:border-electric/50 transition-colors cursor-pointer">
                            <CardContent className="p-6 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-sm bg-electric/10 flex items-center justify-center">
                                    <Plus className="h-6 w-6 text-electric" />
                                </div>
                                <div>
                                    <h4 className="text-white font-medium">Create Content</h4>
                                    <p className="text-white/50 text-sm">Add new article</p>
                                </div>
                            </CardContent>
                        </Card>
                    </Link>
                    <Link to="/admin/campaigns" className="block">
                        <Card className="bg-paper border-border hover:border-solar/50 transition-colors cursor-pointer">
                            <CardContent className="p-6 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-sm bg-solar/10 flex items-center justify-center">
                                    <Megaphone className="h-6 w-6 text-solar" />
                                </div>
                                <div>
                                    <h4 className="text-white font-medium">New Campaign</h4>
                                    <p className="text-white/50 text-sm">Launch SMS/WhatsApp</p>
                                </div>
                            </CardContent>
                        </Card>
                    </Link>
                    <Link to="/admin/predictions" className="block">
                        <Card className="bg-paper border-border hover:border-blue-500/50 transition-colors cursor-pointer">
                            <CardContent className="p-6 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-sm bg-blue-500/10 flex items-center justify-center">
                                    <TrendingUp className="h-6 w-6 text-blue-400" />
                                </div>
                                <div>
                                    <h4 className="text-white font-medium">Add Prediction</h4>
                                    <p className="text-white/50 text-sm">AI-powered analysis</p>
                                </div>
                            </CardContent>
                        </Card>
                    </Link>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminDashboard;
